/**
 * Pure motion planning for the live optimization preview.
 *
 * The preview is intentionally treated as a stream of states rather than as
 * a list of DOM animations.  This keeps matching deterministic when a solver
 * splits or merges a same-site group and lets the React component decide how
 * to render a state when its source or target is outside the viewport.
 */

export const OPTIMIZATION_PREVIEW_MOTION_DURATION_MS = 520;
export const OPTIMIZATION_PREVIEW_MOTION_CATCH_UP_DURATION_MS = 280;
export const OPTIMIZATION_PREVIEW_MOTION_FAST_CATCH_UP_DURATION_MS = 160;
export const OPTIMIZATION_PREVIEW_TRUCK_CHANGE_DURATION_MS = 960;
export const OPTIMIZATION_PREVIEW_MOTION_QUEUE_LIMIT = 4;
export const OPTIMIZATION_PREVIEW_TERMINAL_QUEUE_DELAY_MS = 1360;
// Keep the final boundary visible long enough for the ordered preview queue to
// drain and for the authoritative result to paint, without relying on the
// handoff safety timeout during normal completion.
export const OPTIMIZATION_PREVIEW_TERMINAL_HOLD_DURATION_MS = 2800;
export const OPTIMIZATION_PREVIEW_TERMINAL_HANDOFF_TIMEOUT_MS = 5000;

export function shouldHideOptimizationPreviewInspection(segment, {
  preview = null,
  previewActive = false,
} = {}) {
  const visualPhase = resolveOptimizationPreviewVisualPhase(preview?.activityPhase);
  return previewActive
    && segment?.stopType === "depot_start"
    && visualPhase !== "final-verification"
    && visualPhase !== "result-storage";
}

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeOrderIds(group) {
  const rawOrderIds = Array.isArray(group?.orderIds)
    ? group.orderIds
    : group?.orderIds instanceof Set
      ? Array.from(group.orderIds)
    : Array.isArray(group?.order_ids)
      ? group.order_ids
      : [group?.orderId || group?.order_id];
  const seen = new Set();
  return rawOrderIds
    .map(normalizeText)
    .filter((orderId) => orderId && !seen.has(orderId) && seen.add(orderId));
}

function numericValue(...values) {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return null;
}

function resolveSequence(group) {
  return numericValue(
    group?.sequence,
    group?.sequenceStart,
    group?.sequence_start,
    group?.sequenceStart,
  );
}

function resolveSequenceEnd(group) {
  return numericValue(
    group?.sequenceEnd,
    group?.sequence_end,
    group?.sequenceStart,
    group?.sequence_start,
    group?.sequence,
  );
}

function resolvePosition(group) {
  const position = group?.position && typeof group.position === "object"
    ? group.position
    : group;
  const left = numericValue(
    position?.leftPercent,
    position?.left_percent,
    position?.startPercent,
    position?.start_percent,
    position?.left,
    position?.start,
  );
  const width = numericValue(
    position?.widthPercent,
    position?.width_percent,
    position?.width,
  );
  const right = numericValue(
    position?.rightPercent,
    position?.right_percent,
    position?.right,
    position?.end,
  );
  const resolvedWidth = width === null && left !== null && right !== null ? right - left : width;
  return left !== null && resolvedWidth !== null && resolvedWidth > 0
    ? [left, resolvedWidth]
    : null;
}

function resolveTiming(group) {
  const start = numericValue(
    group?.startMinutes,
    group?.start_minutes,
    group?.displayStartMinutes,
    group?.display_start_minutes,
    group?.start,
  );
  const end = numericValue(
    group?.endMinutes,
    group?.end_minutes,
    group?.displayEndMinutes,
    group?.display_end_minutes,
    group?.end,
  );
  return start !== null || end !== null ? [start, end] : null;
}

export function getOptimizationPreviewMotionIdentity(groupOrMotion) {
  const group = groupOrMotion?.group || groupOrMotion;
  const explicitKey = normalizeText(
    group?.groupId
      || group?.group_id
      || group?.groupKey
      || group?.group_key
      || group?.key,
  );
  if (explicitKey) {
    return `group:${explicitKey}`;
  }
  const orderIds = normalizeOrderIds(group).sort();
  return orderIds.length ? `orders:${orderIds.join(",")}` : "group:unknown";
}

function resolveGroupId(group, index = 0) {
  return normalizeText(
    group?.groupId
      || group?.group_id
      || group?.groupKey
      || group?.group_key
      || group?.key,
  ) || `preview-group-${index + 1}`;
}

function normalizeGroup(group, index) {
  if (!group || typeof group !== "object" || Array.isArray(group)) {
    return null;
  }
  const orderIds = normalizeOrderIds(group);
  if (!orderIds.length) {
    return null;
  }
  return {
    ...group,
    groupId: resolveGroupId(group, index),
    orderIds,
  };
}

function overlapCount(left, right) {
  const rightIds = new Set(normalizeOrderIds(right));
  return normalizeOrderIds(left).filter((orderId) => rightIds.has(orderId)).length;
}

function compareGroups(left, right) {
  const leftSequence = resolveSequence(left);
  const rightSequence = resolveSequence(right);
  if (leftSequence !== null && rightSequence !== null && leftSequence !== rightSequence) {
    return leftSequence - rightSequence;
  }
  return resolveGroupId(left).localeCompare(resolveGroupId(right), "fr", { numeric: true });
}

function findBestPreviousGroup(group, previousGroups) {
	const identity = getOptimizationPreviewMotionIdentity(group);
	const exactMatchIndex = previousGroups.findIndex((previousGroup) => (
		getOptimizationPreviewMotionIdentity(previousGroup) === identity
	));
	if (exactMatchIndex >= 0) {
		return {
			index: exactMatchIndex,
			overlap: overlapCount(group, previousGroups[exactMatchIndex]),
			previousGroup: previousGroups[exactMatchIndex],
		};
	}
	const candidates = previousGroups
    .map((previousGroup, index) => ({
      index,
      overlap: overlapCount(group, previousGroup),
      previousGroup,
    }))
    .filter((candidate) => candidate.overlap > 0)
    .sort((left, right) => (
      right.overlap - left.overlap
      || compareGroups(left.previousGroup, right.previousGroup)
      || left.index - right.index
    ));
  return candidates[0] || null;
}

function hasSameOrderIds(left, right) {
  const leftIds = normalizeOrderIds(left);
  const rightIds = normalizeOrderIds(right);
  return leftIds.length === rightIds.length && leftIds.every((orderId, index) => orderId === rightIds[index]);
}

function hasSamePosition(left, right) {
  const leftPosition = resolvePosition(left);
  const rightPosition = resolvePosition(right);
  if (!leftPosition || !rightPosition) {
    return true;
  }
  return Math.abs(leftPosition[0] - rightPosition[0]) < 0.01
    && Math.abs(leftPosition[1] - rightPosition[1]) < 0.01;
}

function hasSameTiming(left, right) {
  const leftTiming = resolveTiming(left);
  const rightTiming = resolveTiming(right);
  if (!leftTiming || !rightTiming) {
    return true;
  }
  return leftTiming.every((value, index) => (
    value === null || rightTiming[index] === null || Math.abs(value - rightTiming[index]) < 0.01
  ));
}

function isProtectedGroup(group, protectedOrderIds) {
  const orderIds = normalizeOrderIds(group);
  const protectedIds = new Set(protectedOrderIds);
  const explicitProtectedIds = new Set([
    ...(Array.isArray(group?.protectedOrderIds) ? group.protectedOrderIds : []),
    ...(Array.isArray(group?.protected_order_ids) ? group.protected_order_ids : []),
  ].map(normalizeText).filter(Boolean));
  return group?.protected === true
    || group?.isProtected === true
    || group?.is_protected === true
    || orderIds.some((orderId) => protectedIds.has(orderId) || explicitProtectedIds.has(orderId));
}

function resolveMotionKind(group, previousGroup, previousGroups, nextGroups) {
  if (!previousGroup) {
    return "reveal";
  }

  const currentTruckId = normalizeText(group?.truckId || group?.truck_id);
  const previousTruckId = normalizeText(previousGroup?.truckId || previousGroup?.truck_id);
  const truckChanged = currentTruckId && previousTruckId && currentTruckId !== previousTruckId;
  const currentOrderIds = normalizeOrderIds(group);
  const previousOrderIds = normalizeOrderIds(previousGroup);
  const nextOverlapCount = nextGroups.filter((candidate) => overlapCount(previousGroup, candidate) > 0).length;
  const previousOverlapCount = previousGroups.filter((candidate) => overlapCount(candidate, group) > 0).length;

  // A change of truck is the most important movement for a dispatcher to
  // follow. Keep that semantic even when the same update also splits or merges
  // a visual group so it receives the longer, unmistakable flight.
  if (truckChanged) {
    return "truck-change";
  }
  if (nextOverlapCount > 1 && currentOrderIds.length < previousOrderIds.length) {
    return "split";
  }
  if (previousOverlapCount > 1 && currentOrderIds.length > previousOrderIds.length) {
    return "merge";
  }
  if (currentOrderIds.length < previousOrderIds.length) {
    return "split";
  }
  if (currentOrderIds.length > previousOrderIds.length) {
    return "merge";
  }
  const sequenceChanged = resolveSequence(group) !== null
    && resolveSequence(previousGroup) !== null
    && (resolveSequence(group) !== resolveSequence(previousGroup)
      || resolveSequenceEnd(group) !== resolveSequenceEnd(previousGroup));
  const explicitReorder = group?.reordered === true || group?.is_reordered === true;
  const explicitMove = group?.moved === true || group?.is_moved === true;
  if (explicitReorder || sequenceChanged || !hasSameOrderIds(group, previousGroup)) {
    return "reorder";
  }
  if (explicitMove || !hasSamePosition(group, previousGroup)) {
    return "move";
  }
  if (!hasSameTiming(group, previousGroup)) {
    return "retime";
  }
  return "unchanged";
}

function buildMotion(kind, group, previousGroup, extra = {}) {
  const identity = getOptimizationPreviewMotionIdentity(group || previousGroup);
  const normalizedKind = kind === "unchanged" ? "unchanged" : kind;
  return {
    ...extra,
    animate: normalizedKind !== "unchanged" && normalizedKind !== "protected",
    from: extra.from || (previousGroup ? "previous" : "reserve"),
    group: group || null,
    groupId: resolveGroupId(group || previousGroup),
    identity,
    kind: normalizedKind,
    motion: normalizedKind,
    previousGroup: previousGroup || null,
    to: extra.to || (group ? "target" : "reserve"),
  };
}

/**
 * Plan the transition between two preview payloads.
 *
 * `initialRetraction` is intentionally separate from `previousGroups`: the
 * first preview state starts from the committed dispatch and is retracted to
 * the reserve exactly once. Later states use the real geometry of the prior
 * preview, never the reserve by default.
 */
export function buildOptimizationPreviewMotionPlan({
  initialRetraction = false,
  maxQueue = OPTIMIZATION_PREVIEW_MOTION_QUEUE_LIMIT,
  nextGroups = [],
  operationKind = "optimization",
  previousGroups = [],
  protectedOrderIds = [],
  retainedOrderIds = [],
} = {}) {
  const normalizedPreviousGroups = previousGroups.map(normalizeGroup).filter(Boolean);
  const normalizedNextGroups = nextGroups.map(normalizeGroup).filter(Boolean);
  const protectedIds = normalizeOrderIds({
    orderIds: protectedOrderIds instanceof Set ? Array.from(protectedOrderIds) : protectedOrderIds,
  });
  const retainedIds = new Set(normalizeOrderIds({
    orderIds: retainedOrderIds instanceof Set ? Array.from(retainedOrderIds) : retainedOrderIds,
  }));
  const keepsGroupStationary = (group) => normalizeOrderIds(group).some((orderId) => retainedIds.has(orderId))
    || (operationKind !== "planning_reset" && isProtectedGroup(group, protectedIds));
  const decisions = [];
  const matchedPreviousIds = new Set();

  if (initialRetraction) {
    normalizedPreviousGroups.forEach((group) => {
      const kind = keepsGroupStationary(group) ? "protected" : "initial-retraction";
      decisions.push(buildMotion(kind, null, group, {
        from: "committed",
        to: "reserve",
      }));
    });
  }

  normalizedNextGroups.forEach((group) => {
    const bestMatch = findBestPreviousGroup(group, normalizedPreviousGroups);
    const previousGroup = bestMatch?.previousGroup || null;
    if (previousGroup) {
      matchedPreviousIds.add(getOptimizationPreviewMotionIdentity(previousGroup));
    }
    const kind = keepsGroupStationary(group)
      ? "protected"
      : resolveMotionKind(group, previousGroup, normalizedPreviousGroups, normalizedNextGroups);
    decisions.push(buildMotion(kind, group, previousGroup, {
      overlap: bestMatch?.overlap || 0,
      from: previousGroup ? "previous" : "reserve",
      to: "target",
    }));
  });

  normalizedPreviousGroups.forEach((previousGroup) => {
    const identity = getOptimizationPreviewMotionIdentity(previousGroup);
    if (matchedPreviousIds.has(identity) || initialRetraction) {
      return;
    }
    const hasOverlap = normalizedNextGroups.some((group) => overlapCount(group, previousGroup) > 0);
    if (!hasOverlap && !keepsGroupStationary(previousGroup)) {
      decisions.push(buildMotion("retract", null, previousGroup, {
        from: "previous",
        to: "reserve",
      }));
    }
  });

  // `maxQueue` applies to revision batches in the DOM hook. A single revision
  // must keep every segment motion (including a large initial retraction), so
  // never truncate `motions` here.
  const motions = decisions.filter((decision) => decision.animate);
  const condensedMotions = motions;
  return {
    decisions,
    motions: condensedMotions,
    queue: condensedMotions,
  };
}

export function planOptimizationPreviewMotions(options = {}) {
  return buildOptimizationPreviewMotionPlan(options).motions;
}

/**
 * Keep the event queue ordered without discarding states that still need to be
 * painted. `maxQueue` and `replace` remain accepted for callers from older
 * builds, but a preview backlog is authoritative and must never be truncated
 * or replaced by the final boundary.
 */
export function condenseOptimizationPreviewMotionQueue(
  queue = [],
  incoming = [],
  maxQueue = OPTIMIZATION_PREVIEW_MOTION_QUEUE_LIMIT,
  { replace = false } = {},
) {
  const combined = [
    ...(Array.isArray(queue) ? queue : []),
    ...(Array.isArray(incoming) ? incoming : []),
  ];
  const isRevisionBatch = combined.some((item) => Array.isArray(item?.motions));
  if (isRevisionBatch) {
    const latestByRevision = new Map();
    combined.forEach((batch, index) => {
      if (!batch || typeof batch !== "object" || !Array.isArray(batch.motions)) {
        return;
      }
      const key = normalizeText(batch.key)
        || `${normalizeText(batch.jobId)}:${String(batch.revision ?? index)}`;
      latestByRevision.set(key, { batch, index });
    });
    return Array.from(latestByRevision.values())
      .sort((left, right) => left.index - right.index)
      .map(({ batch }) => batch);
  }
  return combined.filter((motion) => motion && typeof motion === "object");
}

export function resolveOptimizationPreviewMotionDuration({
  catchUp = false,
  motionKind = "",
  queueDepth = 0,
} = {}) {
  const normalizedQueueDepth = Math.max(0, Number(queueDepth) || 0);
  if (normalizedQueueDepth > 8) {
    return OPTIMIZATION_PREVIEW_MOTION_FAST_CATCH_UP_DURATION_MS;
  }
  if (catchUp || normalizedQueueDepth >= 3) {
    return OPTIMIZATION_PREVIEW_MOTION_CATCH_UP_DURATION_MS;
  }
  return normalizeText(motionKind).toLowerCase() === "truck-change"
    ? OPTIMIZATION_PREVIEW_TRUCK_CHANGE_DURATION_MS
    : OPTIMIZATION_PREVIEW_MOTION_DURATION_MS;
}

export function resolveOptimizationPreviewVisualPhase(candidate) {
  const stageKey = normalizeText(candidate?.stageKey || candidate?.stage_key || candidate).toLowerCase();
  if (stageKey === "assignments") {
    return "assignments";
  }
  if (stageKey === "dispatch_improvement" || stageKey === "dispatch-improvement") {
    return "dispatch-improvement";
  }
  if (stageKey === "final_verification" || stageKey === "final-verification") {
    return "final-verification";
  }
  if (stageKey === "result_storage" || stageKey === "result-storage") {
    return "result-storage";
  }
  return "settled";
}

function resolvePreviewRevision(candidate) {
  const revision = Number(candidate?.revision || 0);
  return Number.isFinite(revision) && revision > 0 ? revision : 0;
}

/**
 * Coalesce duplicate preview states without dropping real states. The status
 * endpoint is an ordered event stream: when the client falls behind, every
 * distinct revision must remain available for the presentation drain.
 */
export function condenseOptimizationPreviewRevisionQueue(
  queue = [],
  incoming = [],
  maxQueue = OPTIMIZATION_PREVIEW_MOTION_QUEUE_LIMIT,
) {
  const latestByRevision = new Map();
  [...(Array.isArray(queue) ? queue : []), ...(Array.isArray(incoming) ? incoming : [])]
    .forEach((candidate, index) => {
      if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
        return;
      }
      const revision = resolvePreviewRevision(candidate);
      if (!revision) {
        return;
      }
      latestByRevision.set(revision, { candidate, index });
    });
  return Array.from(latestByRevision.entries())
    .sort((left, right) => left[0] - right[0] || left[1].index - right[1].index)
    .map(([, entry]) => entry.candidate);
}

export function isCanonicalOptimizationTerminalPreview(candidate) {
  return resolvePreviewRevision(candidate) > 0
    && normalizeText(candidate?.stageKey || candidate?.stage_key).toLowerCase() === "final_verification"
    && normalizeText(candidate?.source).toLowerCase() === "final_boundary";
}

/**
 * Keep terminal-preview eligibility scoped to the current mounted Dispatch
 * visit. A completed job can remain in the shared tracker after Dispatch is
 * unmounted; it must not look like a newly observed optimization when the
 * page is mounted again.
 */
export function updateOptimizationPreviewVisitState(current = {}, { jobId = "", active = false } = {}) {
  const normalizedJobId = normalizeText(jobId);
  if (!normalizedJobId) {
    return { jobId: "", observedActive: false };
  }

  if (current?.jobId !== normalizedJobId) {
    return {
      jobId: normalizedJobId,
      observedActive: active === true,
    };
  }

  return {
    jobId: normalizedJobId,
    observedActive: current?.observedActive === true || active === true,
  };
}

export function shouldPrioritizeOptimizationTerminalPreview({
  assignmentPreview = null,
  observedActive = true,
  previewMode = "off",
  resultCommitted = false,
  status = "idle",
} = {}) {
  const normalizedStatus = normalizeText(status).toLowerCase();
  const terminalResult = normalizedStatus === "completed"
    || (normalizedStatus === "cancelled" && resultCommitted === true);
  return normalizeText(previewMode).toLowerCase() !== "off"
    && resultCommitted === true
    && terminalResult
    && observedActive === true
    && isCanonicalOptimizationTerminalPreview(assignmentPreview);
}

export function shouldDeferOptimizationTerminalPreview({
  assignmentPreview = null,
  prioritizeTerminal = false,
} = {}) {
  return isCanonicalOptimizationTerminalPreview(assignmentPreview)
    && prioritizeTerminal !== true;
}

export function shouldHoldOptimizationTerminalPreview({
  assignmentPreview = null,
  authoritativePaintSeen = false,
  minimumDisplayElapsed = false,
  observedActive = true,
  previewMode = "off",
  reducedMotion = false,
  resultCommitted = false,
  status = "idle",
  terminalPreviewDisplayed = false,
  timedOut = false,
} = {}) {
  if (!shouldPrioritizeOptimizationTerminalPreview({
    assignmentPreview,
    observedActive,
    previewMode,
    resultCommitted,
    status,
  })) {
    return false;
  }
  if (authoritativePaintSeen && timedOut) {
    return false;
  }
  if (!terminalPreviewDisplayed) {
    return true;
  }
  return !authoritativePaintSeen || (!reducedMotion && !minimumDisplayElapsed);
}

export function updateOptimizationPreviewPresentation(current = {}, {
  assignmentPreview = null,
  deferTerminal = false,
  jobId = "",
  now = Date.now(),
  prioritizeTerminal = false,
} = {}) {
  const normalizedJobId = normalizeText(jobId);
  const revision = resolvePreviewRevision(assignmentPreview);
  if (!normalizedJobId || !assignmentPreview || typeof assignmentPreview !== "object" || !revision) {
    return current;
  }

  const nowValue = Number.isFinite(Number(now)) ? Number(now) : Date.now();
  if (current?.jobId !== normalizedJobId || !current?.preview) {
    return { displayedAt: nowValue, jobId: normalizedJobId, preview: assignmentPreview, queue: [] };
  }

  const currentRevision = resolvePreviewRevision(current.preview);
  if (revision < currentRevision) {
    return current;
  }
  const queuedRevisions = (Array.isArray(current.queue) ? current.queue : [])
    .map((candidate) => resolvePreviewRevision(candidate));
  const highestQueuedRevision = queuedRevisions.length ? Math.max(...queuedRevisions) : 0;
  if (revision <= highestQueuedRevision) {
    return current;
  }
  if (prioritizeTerminal && isCanonicalOptimizationTerminalPreview(assignmentPreview)) {
    if (revision === currentRevision && !current.queue?.length) {
      return { ...current, preview: assignmentPreview };
    }
    // The canonical final boundary is still an event in the stream. Keep the
    // states already waiting in front of it; terminal priority changes its
    // handoff timing, never the ordering of the backlog.
    return {
      ...current,
      queue: condenseOptimizationPreviewRevisionQueue(
        current.queue,
        [assignmentPreview],
        OPTIMIZATION_PREVIEW_MOTION_QUEUE_LIMIT,
      ),
    };
  }
  if (revision === currentRevision) {
    return { ...current, preview: assignmentPreview };
  }

  const currentQueue = Array.isArray(current.queue) ? current.queue : [];
  const minimumDisplayMs = resolveOptimizationPreviewMotionDuration({
    queueDepth: currentQueue.length,
  });
  if (!currentQueue.length && nowValue - Number(current.displayedAt || 0) >= minimumDisplayMs) {
    return { displayedAt: nowValue, jobId: normalizedJobId, preview: assignmentPreview, queue: [] };
  }
  return {
    ...current,
    queue: condenseOptimizationPreviewRevisionQueue(
      currentQueue,
      [assignmentPreview],
      OPTIMIZATION_PREVIEW_MOTION_QUEUE_LIMIT,
    ),
  };
}

const ACTIVITY_KEY_LABELS = Object.freeze({
  assignment: "Affectations",
  assignments: "Affectations",
  assignment_preview: "Affectations",
  dispatch_repair: "Réparation du dispatch",
  dispatch_improvement: "Amélioration du Dispatch",
  final_validation: "Vérification finale",
  final_verification: "Vérification finale",
  greedy_fallback: "Greedy prend le relais",
  greedy_recovery: "Greedy récupère les commandes restantes",
  preparation: "Préparation des données",
  result_storage: "Enregistrement du résultat",
  route_timing: "Calcul des horaires",
  routes: "Calcul des routes",
  sequence: "Réordonnancement des tournées",
  timeline: "Mise à jour de la timeline",
  ortools_search: "OR-Tools améliore les affectations",
});

const PRODUCER_LABELS = Object.freeze({
  dispatch_repair: "Réparation du dispatch",
  greedy: "Greedy",
  greedy_fallback: "Greedy prend le relais",
  greedy_recovery: "Greedy récupère les commandes restantes",
  ortools: "OR-Tools",
  ortools_search: "OR-Tools améliore les affectations",
  ortools_solver: "OR-Tools",
  repair_boundary: "Réparation",
  solver_boundary: "Solveur",
  solver_solution: "Solveur",
  validated_checkpoint: "Checkpoint validé",
});

function humanizeActivityKey(value) {
  const normalized = normalizeText(value).toLowerCase();
  if (!normalized) {
    return "";
  }
  if (ACTIVITY_KEY_LABELS[normalized]) {
    return ACTIVITY_KEY_LABELS[normalized];
  }
  return normalized
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function humanizeProducer(value) {
  const normalized = normalizeText(value).toLowerCase();
  return PRODUCER_LABELS[normalized] || humanizeActivityKey(normalized);
}

/** Resolve the card's synchronized activity label across camel/snake payloads. */
export function resolveOptimizationPreviewActivityLabel(candidate, {
  fallbackSource = "",
  fallbackStage = "",
} = {}) {
  const source = candidate && typeof candidate === "object" ? candidate : {};
  const producer = humanizeProducer(
    source.producer
      || source.producerKey
      || source.producer_key
      || source.sourceProducer
      || source.source_producer,
  );
  const rawProducerKey = normalizeText(
    source.producer
      || source.producerKey
      || source.producer_key
      || source.sourceProducer
      || source.source_producer,
  ).toLowerCase();
  const rawActivityKey = normalizeText(
    source.activityKey
      || source.activity_key
      || source.activity
      || source.activityName
      || source.activity_name,
  ).toLowerCase();
  const activity = humanizeActivityKey(
    rawActivityKey,
  );
  const compositeActivityKey = rawProducerKey && rawActivityKey
    ? `${rawProducerKey}_${rawActivityKey}`
    : "";
  if (rawActivityKey && ACTIVITY_KEY_LABELS[rawActivityKey]) {
    return activity;
  }
  if (compositeActivityKey && ACTIVITY_KEY_LABELS[compositeActivityKey]) {
    return ACTIVITY_KEY_LABELS[compositeActivityKey];
  }
  if (producer && activity && producer.toLowerCase() !== activity.toLowerCase()) {
    return `${producer} · ${activity}`;
  }
  if (producer || activity) {
    return producer || activity;
  }
  const stage = humanizeActivityKey(source.stageKey || source.stage_key || fallbackStage);
  if (stage) {
    return stage;
  }
  const sourceLabel = humanizeProducer(source.source || fallbackSource);
  return sourceLabel || "Source / exécution";
}
